/**
 * IMPORTS
 */
import { CalendarDays, Clock, UserRound } from "lucide-react";

import type { ICompromissoAgenda } from "@/interface/agenda-cliente";

import {
  ehCompromissoDeHoje,
  formatarDataCompromisso,
  formatarHoraCompromisso,
  obterInfoStatus,
  obterInfoTipo,
} from "../helpers/formatadores-agenda";

type CompromissoItemProps = {
  compromisso: ICompromissoAgenda;
};

/** Um compromisso da agenda (somente leitura). Atrasados e de hoje ganham destaque. */
function CompromissoItem({ compromisso }: CompromissoItemProps) {
  const { label: labelTipo, Icone } = obterInfoTipo(compromisso.tipo);
  const status = obterInfoStatus(compromisso.status);
  const atrasado = compromisso.atrasado;
  const hoje = !atrasado && ehCompromissoDeHoje(compromisso);
  const inativo = compromisso.status !== "pendente";

  const destaque = atrasado
    ? "border-l-4 border-l-red-500 bg-red-50/40"
    : hoje
      ? "border-l-4 border-l-orange-500 bg-orange-50/40"
      : "";

  return (
    <article className={`flex gap-3 px-4 py-3 ${destaque}`}>
      <span
        className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${
          inativo ? "bg-gray-100 text-gray-400" : "bg-gray-100 text-gray-700"
        }`}
        title={labelTipo}
      >
        <Icone className="size-4" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {labelTipo}
          </span>
          {atrasado && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              Atrasado
            </span>
          )}
          {hoje && (
            <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700">
              Hoje
            </span>
          )}
          <span className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${status.classe}`}>
            {status.label}
          </span>
        </div>

        <h3
          className={`mt-1 break-words text-base font-medium ${
            compromisso.status === "cancelado" ? "text-gray-500 line-through" : "text-gray-900"
          }`}
        >
          {compromisso.titulo}
        </h3>

        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
          <span className="flex items-center gap-1.5">
            <CalendarDays className="size-4 shrink-0" aria-hidden="true" />
            <time dateTime={compromisso.agendado_para}>
              {formatarDataCompromisso(compromisso.agendado_para)}
            </time>
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="size-4 shrink-0" aria-hidden="true" />
            {formatarHoraCompromisso(compromisso.agendado_para)}
          </span>
          {compromisso.responsavel && (
            <span className="flex items-center gap-1.5">
              <UserRound className="size-4 shrink-0" aria-hidden="true" />
              <span>
                <span className="sr-only">Responsável: </span>
                {compromisso.responsavel.nome}
              </span>
            </span>
          )}
        </div>

        {compromisso.descricao && (
          <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">
            {compromisso.descricao}
          </p>
        )}
      </div>
    </article>
  );
}

/**
 * EXPORTS
 */
export { CompromissoItem };
